import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMember } from '@/lib/use-session';
import {
  CirclesChrome,
  CIRCLE_FORMATS,
  type CircleFormat,
} from '@/components/circles/CirclesChrome';
import { GroupsListBody } from '@/components/group/index';
import { groupSummaryToCircleSummary } from '@/components/circles/adapters';
import type { CircleSummary } from '@/components/circles/sample-data';
import {
  groupKeys,
  type ListCirclesArgs,
} from '@/lib/queries/groups';

const PAGE_SIZE = 24;
// The "mine" rail is a complete list, not a browse page — fetch a window
// large enough that no member's own circles are silently truncated.
const MINE_PAGE_SIZE = 100;

type Scope = 'all' | 'mine';

type CirclesSearch = {
  q?: string;
  topic?: string;
  format?: CircleFormat[];
  language?: string;
  placeText?: string;
  placeCountryCode?: string;
  scope?: Scope;
};

const FORMATS = new Set<CircleFormat>(CIRCLE_FORMATS);
const SCOPES = new Set<Scope>(['all', 'mine']);

// Format is a multi-select: the URL param arrives as a single value or an
// array (see `enumList` in CirclesChrome). Parse to a canonical `CircleFormat[]`
// so it round-trips with the filter chrome and joins to a CSV for the API.
function parseFormats(v: unknown): CircleFormat[] {
  const raw = Array.isArray(v) ? v : v === undefined ? [] : [v];
  return raw.filter((s): s is CircleFormat => typeof s === 'string' && FORMATS.has(s as CircleFormat));
}
function parseScope(v: unknown): Scope | undefined {
  return typeof v === 'string' && SCOPES.has(v as Scope) ? (v as Scope) : undefined;
}
function pickStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

export const Route = createFileRoute('/_public/circles/')({
  validateSearch: (raw: Record<string, unknown>): CirclesSearch => ({
    ...(pickStr(raw.q) !== undefined ? { q: pickStr(raw.q) as string } : {}),
    ...(pickStr(raw.topic) !== undefined ? { topic: pickStr(raw.topic) as string } : {}),
    ...(parseFormats(raw.format).length > 0 ? { format: parseFormats(raw.format) } : {}),
    ...(pickStr(raw.language) !== undefined ? { language: pickStr(raw.language) as string } : {}),
    ...(pickStr(raw.placeText) !== undefined ? { placeText: pickStr(raw.placeText) as string } : {}),
    ...(pickStr(raw.placeCountryCode) !== undefined ? { placeCountryCode: pickStr(raw.placeCountryCode) as string } : {}),
    ...(parseScope(raw.scope) !== undefined ? { scope: parseScope(raw.scope) as Scope } : {}),
  }),
  component: CirclesListPage,
});

function CirclesListPage() {
  const search = Route.useSearch();
  const member = useMember();
  const signedIn = member !== null;

  // Browse defaults to the ranked discovery sort (completeness + viewer
  // similarity). An active search query switches to the server's recency
  // default. The server drops the similarity term for anonymous viewers, so
  // passing `sort: 'recommended'` is safe either way.
  const listArgs: ListCirclesArgs = {
    pageSize: PAGE_SIZE,
    ...(search.q === undefined ? { sort: 'recommended' as const } : {}),
    ...(search.q !== undefined ? { q: search.q } : {}),
    ...(search.topic !== undefined ? { topic: search.topic } : {}),
    ...(search.format !== undefined && search.format.length > 0
      ? { format: search.format.join(',') }
      : {}),
    ...(search.language !== undefined ? { language: search.language } : {}),
    ...(search.placeText !== undefined ? { placeText: search.placeText } : {}),
    ...(search.placeCountryCode !== undefined ? { placeCountryCode: search.placeCountryCode } : {}),
  };

  // Mine drives the "My Circles" tab. Disabled when signed out: the endpoint
  // requires auth and the data is irrelevant to the anon directory.
  const myCirclesQuery = useQuery({
    ...groupKeys.circle.mine({ includeArchived: true, pageSize: MINE_PAGE_SIZE }),
    enabled: signedIn,
  });

  const mine: readonly CircleSummary[] | null = myCirclesQuery.data
    ? myCirclesQuery.data.items.map((m) =>
        groupSummaryToCircleSummary(m, { role: m.role }),
      )
    : null;

  return (
    <CirclesChrome signedIn={signedIn}>
      <GroupsListBody
        kind="circle"
        listArgs={listArgs}
        mine={mine}
        isError={myCirclesQuery.isError}
        signedIn={signedIn}
      />
    </CirclesChrome>
  );
}
